import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, KeyRound, User as UserIcon, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", username: user?.username || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateProfile(form);
      toast.success("Profile updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
    finally { setSavingProfile(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (!pw.current_password || !pw.new_password) { toast.error("Both passwords required"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password changed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSavingPw(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <h1 className="display text-4xl">Profile</h1>
        <p className="text-slate-500 mt-2">Your account, your way.</p>
      </div>

      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4"><UserIcon className="w-5 h-5 text-[#0044FF]" /><h3 className="display text-xl">Account info</h3></div>
        <form onSubmit={saveProfile} className="space-y-4 max-w-md">
          <div><Label>Email</Label><Input value={user?.email || ""} disabled className="bg-slate-50" /></div>
          <div><Label>Full name</Label><Input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Username</Label><Input data-testid="profile-username" value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="@handle" /></div>
          <div><Label>Role</Label><Input value={user?.role || ""} disabled className="bg-slate-50 mono uppercase tracking-wider" /></div>
          <Button data-testid="profile-save" type="submit" disabled={savingProfile} className="bg-[#0044FF] hover:bg-[#0033CC]">
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
        </form>
      </Card>

      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4"><KeyRound className="w-5 h-5 text-[#0044FF]" /><h3 className="display text-xl">Change password</h3></div>
        <form onSubmit={changePw} className="space-y-4 max-w-md">
          <div><Label>Current password</Label><Input data-testid="profile-current-password" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></div>
          <div><Label>New password</Label><Input data-testid="profile-new-password" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></div>
          <Button data-testid="profile-change-password" type="submit" disabled={savingPw} className="bg-[#0044FF] hover:bg-[#0033CC]">
            {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change password"}
          </Button>
        </form>
      </Card>

      <Card className="p-6 border-slate-200 glass-blue">
        <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5 text-[#0044FF]" /><h3 className="display text-lg">AI Credits</h3></div>
        <div className="display text-3xl">$24.80</div>
        <p className="text-sm text-slate-500 mt-1">Powered by Emergent Universal Key (Gemini)</p>
      </Card>
    </div>
  );
}
